import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { CHAINS } from '../config/chains';
import { estimateTransferGasCost } from '../utils/gasEstimation';

const ERC20_BALANCE_ABI = ['function balanceOf(address) view returns (uint256)'];

// Re-checks in the background so a chip/CTA that's been open a while doesn't
// go stale — balances (especially gas) can move between visits.
const REFRESH_MS = 30000;

const isZeroAddress = (address) =>
    !address || /^0x0+$/i.test(address);

const tokenShape = (t) => ({
    symbol: t.symbol,
    name: t.name,
    icon: t.icon,
    decimals: t.decimals,
    address: t.address,
});

/**
 * Same thresholds as the design reference (usernames-desktop/mobile.dc.html):
 * empty at zero transfers of headroom, low under three, ok otherwise. There
 * it was gasRuns from a mock GAS_PER_TX constant — here gasPerTx is real.
 */
const deriveGasState = (gasRuns) => {
    if (gasRuns == null) return null;
    if (gasRuns <= 0) return 'empty';
    if (gasRuns < 3) return 'low';
    return 'ok';
};

/**
 * Real values behind the balances-and-gas UI: native gas balance, wallet
 * ERC-20 rows for the selected chain (USDC/USDT/CUSD/USDG/USDe/…), and the
 * derived gasState. Confidential balances are not read here — the app already
 * tracks those (App.js `confidentialBalance`).
 *
 * @param {{ provider: import('ethers').providers.Provider|null, account: string|null, chainKey: string|null }} args
 *   chainKey is a CHAINS key (e.g. 'arbitrum_mainnet'), not an EVM chain id.
 */
export function useBalances({ provider, account, chainKey }) {
    const chain = chainKey ? CHAINS[chainKey] : null;

    const [state, setState] = useState({
        loading: false,
        nativeBalance: null, // number, native units (e.g. ETH) — null while unknown
        gasPerTx: null, // number, native units per confidential transfer
        gasRuns: null, // Math.floor(nativeBalance / gasPerTx)
        gasState: null, // 'ok' | 'low' | 'empty' | null (null = not known yet)
        wallet: {},
    });

    // Guards against a slow read for a since-changed account/chain landing late.
    const requestIdRef = useRef(0);

    const load = useCallback(async () => {
        if (!account || !chain) {
            requestIdRef.current += 1;
            setState({
                loading: false,
                nativeBalance: null,
                gasPerTx: null,
                gasRuns: null,
                gasState: null,
                wallet: {},
            });
            return;
        }

        const requestId = (requestIdRef.current += 1);
        setState((prev) => ({ ...prev, loading: true }));

        // Read through the selected chain's own public RPC, not the wallet
        // provider — the wallet can still be pointed at a different network
        // (same reason TabbedOperations.updateTokenBalance does this).
        const readProvider = chain.rpcUrl
            ? new ethers.providers.JsonRpcProvider(chain.rpcUrl)
            : provider;

        const readErc20 = async (tokenKey) => {
            const token = chain.tokens?.[tokenKey];
            if (!token || isZeroAddress(token.address) || token.active === false) return null;
            const shape = tokenShape(token);
            if (!readProvider) return { ...shape, balance: null };
            try {
                const erc20 = new ethers.Contract(token.address, ERC20_BALANCE_ABI, readProvider);
                const raw = await erc20.balanceOf(account);
                const balance = parseFloat(ethers.utils.formatUnits(raw, token.decimals ?? 6));
                return { ...shape, balance: Number.isFinite(balance) ? balance : null };
            } catch (error) {
                console.warn(`[useBalances] wallet ${tokenKey} balance read failed:`, error?.message || error);
                return { ...shape, balance: null };
            }
        };

        const readNative = async () => {
            if (!readProvider) return null;
            try {
                const raw = await readProvider.getBalance(account);
                const value = parseFloat(ethers.utils.formatEther(raw));
                return Number.isFinite(value) ? value : null;
            } catch (error) {
                console.warn('[useBalances] native balance read failed:', error?.message || error);
                return null;
            }
        };

        const tokenKeys = Object.keys(chain.tokens || {});
        const [nativeBalance, gasPerTx, ...tokenBalances] = await Promise.all([
            readNative(),
            estimateTransferGasCost(readProvider).catch(() => null),
            ...tokenKeys.map((key) => readErc20(key)),
        ]);

        // A newer call already landed (account/chain changed mid-flight) — drop this one.
        if (requestId !== requestIdRef.current) return;

        const gasRuns =
            nativeBalance != null && gasPerTx != null && gasPerTx > 0
                ? Math.floor(nativeBalance / gasPerTx)
                : null;

        const wallet = {};
        tokenKeys.forEach((key, i) => {
            wallet[key] = tokenBalances[i];
        });

        setState({
            loading: false,
            nativeBalance,
            gasPerTx,
            gasRuns,
            gasState: deriveGasState(gasRuns),
            wallet,
        });
    }, [account, chain, provider]);

    useEffect(() => {
        load();
        if (!account || !chain) return undefined;
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load, account, chain]);

    return useMemo(
        () => ({
            ...state,
            nativeSymbol: chain?.nativeCurrency?.symbol || 'ETH',
            chainName: chain?.name || null,
            refresh: load,
        }),
        [state, chain, load],
    );
}

export default useBalances;
